import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Compass, ShoppingBag, Palette, Wand2, Users, Trophy, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/context/LanguageContext';
import api, { assetUrl } from '@/lib/api';
import SEO from '@/components/SEO';

const POPULAR = [
  { icon: ShoppingBag, tk: 'nav.shop', to: '/shop', hueA: '#e6248a', hueB: '#ff6b35' },
  { icon: Palette, tk: 'nav.designs', to: '/designs', hueA: '#0ea5e9', hueB: '#22d3ee' },
  { icon: Wand2, tk: 'nav.aiGenerator', to: '/ai-generator', hueA: '#a855f7', hueB: '#ec4899' },
  { icon: Trophy, tk: 'nav.topRated', to: '/top-rated', hueA: '#f59e0b', hueB: '#fb923c' },
  { icon: Users, tk: 'nav.community', to: '/community', hueA: '#10b981', hueB: '#34d399' },
  { icon: Home, tk: 'nav.home', to: '/', hueA: '#6366f1', hueB: '#8b5cf6' },
];

/**
 * Global "page not found" landing. Instead of a dead-end, we lean into
 * search + curated links so a lost visitor lands back on a page they can act
 * on. Search hits the existing `/designs?search=` and `/products` endpoints —
 * no new backend surface required.
 */
const NotFound = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [designs, setDesigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Preload a tiny set of trending results so the page has visual content
    // for users who arrive without a query.
    api.get('/top-rated?limit=6').then(({ data }) => setTrending((data || []).slice(0, 6))).catch(() => {});
  }, []);

  const runSearch = useCallback(async (query) => {
    const term = (query ?? q).trim();
    if (!term) { setDesigns([]); setProducts([]); return; }
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        api.get(`/designs?search=${encodeURIComponent(term)}&limit=8`).then((r) => r.data).catch(() => null),
        api.get('/products').then((r) => r.data).catch(() => []),
      ]);
      const lower = term.toLowerCase();
      // /designs returns { items, total, ... }; /products returns a flat array.
      const designList = Array.isArray(d) ? d : (d?.items || []);
      setDesigns(designList.slice(0, 8));
      setProducts((Array.isArray(p) ? p : []).filter((x) => (x.name || '').toLowerCase().includes(lower)
        || (x.description || '').toLowerCase().includes(lower)).slice(0, 6));
    } finally { setLoading(false); }
  }, [q]);

  const onSubmit = (e) => { e.preventDefault(); runSearch(); };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <SEO title="Page not found" description="We couldn't find that page — search or explore what's trending on Jwalek." path="/404" />
      <div className="relative overflow-hidden">
        {/* Gradient orbs backdrop for a delightful landing instead of a dry 404. */}
        <div aria-hidden className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-30 blur-3xl jw-gradient-bg" />
        <div aria-hidden className="absolute -bottom-16 -left-16 w-[360px] h-[360px] rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, #22d3ee, transparent 60%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'var(--jw-panel-bg)', color: 'var(--jw-text-muted)' }}>
            <Compass className="w-3.5 h-3.5" />
            {t('notFound.tag', 'Off the map')}
          </div>
          <h1 className="mt-4 font-display font-bold tracking-tight text-6xl sm:text-7xl lg:text-8xl">
            <span className="jw-gradient-text">4</span>
            <span className="jw-text-primary">0</span>
            <span className="jw-gradient-text">4</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl font-medium jw-text-primary">
            {t('notFound.title', "This page took a detour — let's get you back.")}
          </p>
          <p className="mt-2 max-w-xl mx-auto text-sm sm:text-base" style={{ color: 'var(--jw-text-muted)' }}>
            {t('notFound.sub', 'Search our design library, phone cases, stickers and labels — or jump into what everyone else is loving right now.')}
          </p>

          {/* Search */}
          <form onSubmit={onSubmit} className="mt-8 max-w-2xl mx-auto" role="search">
            <label htmlFor="nf-search" className="sr-only">{t('common.search', 'Search')}</label>
            <div className="relative flex items-center rounded-2xl border" style={{ background: 'var(--jw-bg-elev)', borderColor: 'var(--jw-border)' }}>
              <Search className="absolute left-4 w-5 h-5" style={{ color: 'var(--jw-text-muted)' }} />
              <input
                id="nf-search"
                data-testid="not-found-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('notFound.placeholder', 'Search designs, phone models, categories...')}
                className="w-full bg-transparent pl-12 pr-32 py-4 text-base outline-none"
                style={{ color: 'var(--jw-text)' }}
                autoFocus
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="not-found-search-submit-button"
                className="absolute right-2 h-10 px-5 rounded-xl jw-btn-glossy text-sm font-semibold"
              >
                {loading ? t('common.loading', 'Loading...') : t('common.search', 'Search')}
              </Button>
            </div>
          </form>
        </div>

        {/* Search results */}
        {(designs.length > 0 || products.length > 0) && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
            <p className="text-xs uppercase tracking-wider mb-3" style={{ color: 'var(--jw-text-subtle)' }}>
              {t('notFound.resultsFor', 'Results for “{q}”').replace('{q}', q)}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {products.map((p) => (
                <Link key={`p-${p.id}`} to={`/product/${p.id}`} data-testid={`not-found-product-${p.id}-card`}>
                  <Card className="jw-card overflow-hidden group h-full">
                    <div className="aspect-square overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
                      {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    </div>
                    <div className="p-3">
                      <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--jw-text-subtle)' }}>{t('signed.productBadge', 'Product')}</p>
                      <p className="text-sm font-semibold jw-text-primary truncate">{p.name}</p>
                    </div>
                  </Card>
                </Link>
              ))}
              {designs.map((d) => (
                <Link key={`d-${d.id}`} to={`/designs/${d.id}`} data-testid={`not-found-design-${d.id}-card`}>
                  <Card className="jw-card overflow-hidden group h-full">
                    <div className="aspect-square overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
                      {d.image_url && <img src={assetUrl(d.image_url)} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    </div>
                    <div className="p-3">
                      <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--jw-text-subtle)' }}>{t('signed.designBadge', 'Design')}</p>
                      <p className="text-sm font-semibold jw-text-primary truncate">{d.name}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Popular categories */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
          <p className="text-xs uppercase tracking-wider mb-3 text-center" style={{ color: 'var(--jw-text-subtle)' }}>
            {t('notFound.popular', 'Popular destinations')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {POPULAR.map((p) => (
              <button
                key={p.tk}
                onClick={() => navigate(p.to)}
                data-testid={`not-found-popular-${p.tk.replace('.', '-')}-button`}
                className="group relative overflow-hidden rounded-xl p-4 text-left transition-transform hover:-translate-y-0.5"
                style={{ background: 'var(--jw-bg-elev)', border: '1px solid var(--jw-border)' }}
              >
                <div className="w-9 h-9 rounded-lg grid place-items-center text-white shrink-0"
                  style={{ background: `linear-gradient(135deg, ${p.hueA}, ${p.hueB})` }}>
                  <p.icon className="w-4 h-4" />
                </div>
                <p className="mt-3 text-sm font-semibold jw-text-primary">{t(p.tk, p.tk.split('.').pop())}</p>
                <ArrowRight className="w-3.5 h-3.5 mt-1 opacity-60 jw-flip-in-rtl" />
              </button>
            ))}
          </div>
        </div>

        {/* Trending strip when no search entered */}
        {q.trim() === '' && trending.length > 0 && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--jw-text-subtle)' }}>
                {t('signed.trending', 'Trending on Jwalek')}
              </p>
              <Link to="/top-rated" className="text-xs jw-text-muted hover:opacity-80 inline-flex items-center gap-1">
                {t('signed.seeAll', 'See all')} <ArrowRight className="w-3 h-3 jw-flip-in-rtl" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {trending.filter((it) => it && (it.image_url || it.title)).map((item) => (
                <Link
                  key={`${item.entity_type}-${item.id}`}
                  to={item.entity_type === 'design' ? `/designs/${item.id}` : `/product/${item.id}`}
                  data-testid={`not-found-trending-${item.entity_type}-${item.id}`}
                >
                  <Card className="jw-card overflow-hidden h-full">
                    <div className="aspect-square overflow-hidden grid place-items-center" style={{ background: 'var(--jw-bg-elev)' }}>
                      {item.image_url
                        ? <img src={assetUrl(item.image_url)} alt={item.title || ''} className="w-full h-full object-cover" />
                        : <Trophy className="w-8 h-8 opacity-70 text-orange-300" />}
                    </div>
                    <div className="p-2 min-h-[36px] flex items-center">
                      <p className="text-xs jw-text-primary truncate w-full">{item.title || t('common.search', 'Search')}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotFound;
